/**
 * Toast notification component
 * Displays temporary messages with optional action button
 */

import { useEffect, useState } from 'react';
import { CheckCircle, X, ShoppingCart } from 'lucide-react';
import { Link } from 'react-router-dom';

export interface ToastData {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info';
  action?: {
    label: string;
    href: string;
  };
}

interface ToastProps {
  toast: ToastData;
  onDismiss: (id: string) => void;
  duration?: number;
}

export function Toast({ toast, onDismiss, duration = 4000 }: ToastProps): JSX.Element {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    // Trigger enter animation
    requestAnimationFrame(() => setIsVisible(true));

    // Auto dismiss after duration
    const timer = setTimeout(() => {
      handleDismiss();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration]);

  const handleDismiss = () => {
    setIsLeaving(true);
    setTimeout(() => {
      onDismiss(toast.id);
    }, 200);
  };

  return (
    <div
      className={`
        flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border
        bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700
        transform transition-all duration-200
        ${isVisible && !isLeaving ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'}
      `}
    >
      {/* Icon */}
      <div className={`
        p-1.5 rounded-full flex-shrink-0
        ${toast.type === 'success' ? 'bg-green-100 dark:bg-green-900/30' : ''}
        ${toast.type === 'error' ? 'bg-red-100 dark:bg-red-900/30' : ''}
        ${toast.type === 'info' ? 'bg-blue-100 dark:bg-blue-900/30' : ''}
      `}>
        <CheckCircle
          size={18}
          className={`
            ${toast.type === 'success' ? 'text-green-600 dark:text-green-400' : ''}
            ${toast.type === 'error' ? 'text-red-600 dark:text-red-400' : ''}
            ${toast.type === 'info' ? 'text-blue-600 dark:text-blue-400' : ''}
          `}
        />
      </div>

      {/* Message */}
      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
        {toast.message}
      </span>

      {/* Action Button */}
      {toast.action && (
        <Link
          to={toast.action.href}
          className="ml-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
        >
          <ShoppingCart size={14} />
          {toast.action.label}
        </Link>
      )}

      {/* Dismiss Button */}
      <button
        onClick={handleDismiss}
        className="ml-2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
      >
        <X size={16} />
      </button>
    </div>
  );
}

interface ToastContainerProps {
  toasts: ToastData[];
  onDismiss: (id: string) => void;
}

export function ToastContainer({ toasts, onDismiss }: ToastContainerProps): JSX.Element | null {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}
